import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, LoadingSpinner } from './UIComponents';
import { EditableBacklog } from './EditableBacklog';
import { ParsedBacklog } from '../types/story';
import { parseBacklogMarkdown, backlogToMarkdown } from '../utils/backlogParser';
import {
  ExcelImportMode,
  ParsedExcelBacklog,
  applyExcelImportToBacklog,
  parseExcelBacklogFile
} from '../utils/excelBacklogImport';

type ViewMode = 'markdown' | 'edit';

interface BacklogDisplayProps {
  backlog: string | null;
  loading: boolean;
  error: string | null;
  onBacklogUpdate?: (markdown: string) => void;
  onExportExcel?: (backlog: ParsedBacklog) => void;
  onImportFeedback?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
  onParsedBacklogChange?: (parsedBacklog: ParsedBacklog | null) => void;
}

export const BacklogDisplay: React.FC<BacklogDisplayProps> = ({ 
  backlog, 
  loading, 
  error,
  onBacklogUpdate,
  onExportExcel,
  onImportFeedback,
  onParsedBacklogChange
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('markdown');
  const [parsedBacklog, setParsedBacklog] = useState<ParsedBacklog | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState<ExcelImportMode>('replace');
  const [pendingImport, setPendingImport] = useState<ParsedExcelBacklog | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasExistingBacklog = (parsedBacklog?.stories.length || 0) > 0;

  // Parse backlog when it changes
  useEffect(() => {
    if (backlog) {
      try {
        const parsed = parseBacklogMarkdown(backlog);
        setParsedBacklog(parsed);
        onParsedBacklogChange?.(parsed);
        setParseError(null);
      } catch (err) {
        console.error('Error parsing backlog:', err);
        setParseError('No se pudo parsear el backlog para edición');
        setParsedBacklog(null);
        onParsedBacklogChange?.(null);
      }
    } else {
      setParsedBacklog(null);
      onParsedBacklogChange?.(null);
    }
  }, [backlog, onParsedBacklogChange]);

  // Handle backlog changes from editor
  const handleBacklogChange = (updatedBacklog: ParsedBacklog) => {
    setParsedBacklog(updatedBacklog);
    onParsedBacklogChange?.(updatedBacklog);
    
    // Convert back to markdown and notify parent
    if (onBacklogUpdate) {
      const newMarkdown = backlogToMarkdown(updatedBacklog);
      onBacklogUpdate(newMarkdown);
    }
  };

  // Export to markdown
  const handleExportMarkdown = () => {
    if (!parsedBacklog) return;
    
    const markdown = backlogToMarkdown(parsedBacklog);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backlog-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export to Excel (delegate to parent)
  const handleExportExcel = () => {
    if (parsedBacklog && onExportExcel) {
      onExportExcel(parsedBacklog);
    }
  };

  const openImportPicker = () => {
    setImportError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleExcelPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsParsingExcel(true);
    setImportError(null);

    try {
      const parsed = await parseExcelBacklogFile(file);
      setPendingImport(parsed);
      setImportMode(hasExistingBacklog ? 'update_by_id' : 'first_import');
      setShowImportModal(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo leer el archivo Excel.';
      setImportError(message);
      onImportFeedback?.('error', message);
    } finally {
      setIsParsingExcel(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setPendingImport(null);
  };

  const applyExcelImport = () => {
    if (!pendingImport) return;

    const { backlog: importedBacklog, stats } = applyExcelImportToBacklog(parsedBacklog, pendingImport, importMode);
    setParsedBacklog(importedBacklog);
    const markdown = backlogToMarkdown(importedBacklog);
    onBacklogUpdate?.(markdown);
    closeImportModal();

    const modeLabel =
      stats.mode === 'replace' ? 'reemplazó' :
      stats.mode === 'append' ? 'agregó' :
      stats.mode === 'first_import' ? 'primera carga' :
      'actualizó por ID';

    onImportFeedback?.(
      'success',
      `Excel importado: ${stats.imported} historias, ${stats.updated} actualizadas, ${stats.added} agregadas (${modeLabel}).`
    );
  };

  const renderImportButton = () => (
    <button
      onClick={openImportPicker}
      disabled={isParsingExcel}
      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isParsingExcel ? '⏳ Cargando...' : '📥 Importar Excel'}
    </button>
  );

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".xlsx,.xls"
      className="hidden"
      onChange={handleExcelPicked}
    />
  );

  const importModeOptions: Array<{ value: ExcelImportMode; label: string; description: string; disabled?: boolean }> = [
    {
      value: 'first_import',
      label: 'Primera carga',
      description: 'Úsala cuando aún no existe backlog.',
      disabled: hasExistingBacklog
    },
    {
      value: 'replace',
      label: 'Reemplazar',
      description: 'Borra el backlog actual y deja solo el archivo cargado.'
    },
    {
      value: 'append',
      label: 'Agregar',
      description: 'Conserva el backlog actual y adiciona las historias del archivo.'
    },
    {
      value: 'update_by_id',
      label: 'Actualizar IDs',
      description: 'Actualiza historias existentes por ID HU y agrega nuevas no encontradas.'
    }
  ];

  const importModal = showImportModal && pendingImport ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6">
        <h3 className="text-xl font-bold text-gft-dark mb-2">Importar backlog desde Excel</h3>
        <p className="text-sm text-gray-600">
          Archivo: <span className="font-semibold">{pendingImport.filename}</span> · Historias detectadas: <span className="font-semibold">{pendingImport.stories.length}</span>
        </p>

        <div className="mt-5 space-y-3">
          {importModeOptions.map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                option.disabled
                  ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-70'
                  : importMode === option.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="excel-import-mode"
                value={option.value}
                checked={importMode === option.value}
                onChange={() => setImportMode(option.value)}
                className="mt-0.5"
                disabled={option.disabled}
              />
              <div>
                <p className="font-semibold text-gray-800">{option.label}</p>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Este cambio actualiza tanto la vista edición como la vista markdown.
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={closeImportModal}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={applyExcelImport}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-semibold"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <>
        {hiddenFileInput}
        <Card className="min-h-[400px] flex flex-col items-center justify-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 font-semibold">
            Generando backlog con IA...
          </p>
          <p className="mt-2 text-sm text-gray-500">
            El agente está analizando la documentación y generando las historias de usuario
          </p>
        </Card>
        {importModal}
      </>
    );
  }

  if (error) {
    return (
      <>
        {hiddenFileInput}
        <Card className="min-h-[400px] bg-red-50 border-2 border-red-200">
          <div className="flex items-start">
            <span className="text-3xl mr-4">⚠️</span>
            <div>
              <h3 className="text-lg font-bold text-red-800 mb-2">
                Error al generar el backlog
              </h3>
              <p className="text-red-700">{error}</p>
              <div className="mt-4 p-4 bg-red-100 rounded-lg">
                <p className="text-sm text-red-800 font-semibold mb-2">
                  Posibles causas:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Credenciales de AWS no configuradas correctamente</li>
                  <li>Sin acceso al modelo de Amazon Bedrock</li>
                  <li>Ruta de contexto inválida o inaccesible</li>
                  <li>Error de conexión con el backend</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
        {importModal}
      </>
    );
  }

  if (!backlog) {
    return (
      <>
        {hiddenFileInput}
        <Card className="min-h-[400px] flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-white">
          <div className="text-center">
            <span className="text-6xl mb-4 block">📋</span>
            <h3 className="text-2xl font-bold text-gft-dark mb-3">
              Backlog Vacío
            </h3>
            <p className="text-gray-600 max-w-md">
              Carga un directorio con contexto, añade instrucciones adicionales y 
              haz clic en "Generar Backlog" para comenzar.
            </p>
            <div className="mt-4 flex items-center justify-center">
              {renderImportButton()}
            </div>
            {importError && (
              <p className="mt-3 text-sm text-red-600">{importError}</p>
            )}
            <div className="mt-6 p-4 bg-white rounded-lg shadow-sm">
              <p className="text-sm text-gray-700 font-semibold mb-2">
                El backlog incluirá:
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Épicas identificadas</li>
                <li>✓ Historias de Usuario completas (HU-001, HU-002...)</li>
                <li>✓ Criterios de aceptación verificables</li>
                <li>✓ Priorización por valor de negocio</li>
                <li>✓ 4 tareas estándar obligatorias por historia</li>
                <li>✓ Edición interactiva de cada historia</li>
              </ul>
            </div>
          </div>
        </Card>
        {importModal}
      </>
    );
  }

  return (
    <>
      {hiddenFileInput}
      <div className="space-y-4">
      {/* View Mode Toggle */}
      <Card className="!p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('markdown')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === 'markdown'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📄 Vista Markdown
            </button>
            <button
              onClick={() => setViewMode('edit')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === 'edit'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              disabled={!parsedBacklog}
            >
              ✏️ Vista Edición
              {parsedBacklog && (
                <span className="ml-2 px-1.5 py-0.5 bg-white bg-opacity-20 rounded text-xs">
                  {parsedBacklog.stories.length} HU
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-3">
            {renderImportButton()}
            {parseError && (
              <span className="text-sm text-amber-600">
                ⚠️ {parseError}
              </span>
            )}
            {importError && (
              <span className="text-sm text-red-600">
                ⚠️ {importError}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {viewMode === 'edit' && parsedBacklog ? (
        <EditableBacklog
          backlog={parsedBacklog}
          onBacklogChange={handleBacklogChange}
          onExportMarkdown={handleExportMarkdown}
          onExportExcel={handleExportExcel}
        />
      ) : (
        <Card className="min-h-[400px]">
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gft-dark flex items-center">
              <span className="mr-3">📋</span>
              Backlog Generado
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleExportMarkdown}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm"
              >
                💾 Descargar MD
              </button>
              {onExportExcel && (
                <button
                  onClick={handleExportExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                >
                  📊 Exportar Excel
                </button>
              )}
            </div>
          </div>
          
          <div className="prose prose-slate max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-3xl font-bold text-gft-dark mt-6 mb-4">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl font-bold text-gft-blue mt-6 mb-3">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl font-bold text-gray-800 mt-4 mb-2">{children}</h3>
                ),
                p: ({ node, ...props }) => (
                  <p className="text-gray-700 leading-relaxed mb-3" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props} />
                ),
                code: ({ node, inline, ...props }: any) => 
                  inline ? (
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm text-gft-purple font-mono" {...props} />
                  ) : (
                    <code className="block bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto font-mono" {...props} />
                  ),
                hr: ({ node, ...props }) => (
                  <hr className="my-6 border-t-2 border-gray-300" {...props} />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-4 border-gft-purple pl-4 py-2 my-4 bg-purple-50 italic text-gray-700" {...props} />
                ),
              }}
            >
              {backlog}
            </ReactMarkdown>
          </div>
        </Card>
      )}
      </div>
      {importModal}
    </>
  );
};
